// src/components/ErrorBoundary.js
import { Component } from "react";

const CHUNK_RELOAD_KEY = "fv_chunk_reload_at";
const CHUNK_RELOAD_WINDOW_MS = 10_000; // don't loop-reload more than once per 10s

function isChunkError(error) {
  return (
    error?.name === "ChunkLoadError" ||
    /Loading chunk [\d]+ failed/i.test(error?.message || "") ||
    /Loading CSS chunk/i.test(error?.message || "")
  );
}

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, isChunk: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error, isChunk: isChunkError(error) };
  }

  componentDidCatch(error, info) {
    console.error("ErrorBoundary caught:", error, info.componentStack);

    if (isChunkError(error)) {
      const lastReload = parseInt(sessionStorage.getItem(CHUNK_RELOAD_KEY) || "0", 10);
      const now = Date.now();
      if (now - lastReload > CHUNK_RELOAD_WINDOW_MS) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isChunk) {
        // Show a gentle "updating" message while reload fires
        return (
          <div
            style={{
              padding: "40px 24px",
              textAlign: "center",
              color: "var(--text)",
            }}
          >
            <p style={{ fontSize: "2rem", marginBottom: "12px" }}>🔄</p>
            <p style={{ fontWeight: 700, marginBottom: "8px" }}>
              App updated — reloading…
            </p>
            <p style={{ opacity: 0.6, fontSize: "0.85rem", marginBottom: "20px" }}>
              A new version of Marelle Memories is available. The page will refresh automatically.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "8px 20px",
                borderRadius: "10px",
                background: "var(--accent)",
                color: "var(--bg)",
                border: "none",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Reload now
            </button>
          </div>
        );
      }

      return (
        <div
          style={{
            padding: "40px 24px",
            textAlign: "center",
            color: "var(--text)",
          }}
        >
          <p style={{ fontSize: "2rem", marginBottom: "12px" }}>⚠️</p>
          <p style={{ fontWeight: 700, marginBottom: "8px" }}>
            Something went wrong.
          </p>
          <p style={{ opacity: 0.6, fontSize: "0.85rem", marginBottom: "20px" }}>
            {this.state.error?.message || "An unexpected error occurred."}
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null, isChunk: false })}
            style={{
              padding: "8px 20px",
              borderRadius: "10px",
              background: "var(--accent)",
              color: "var(--bg)",
              border: "none",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
